import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, RefreshCw, Activity, ShieldCheck } from 'lucide-react';
import ConfirmationDialog from '../../../common/ConfirmationDialog';

const AptiviewAppStatus = ({
  selectedCompany,
  companies = [],
  onVerificationRequiredChange,
  onShowingIdentityChange,
  isSuperAdmin = false,
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null); // null = loading, true = working, false = not working
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const [updatingVerification, setUpdatingVerification] = useState(false);
  const [updatingShowingIdentity, setUpdatingShowingIdentity] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingVerificationValue, setPendingVerificationValue] = useState(null);

  const checkHealth = async () => {
    const interviewUiUrl = process.env.REACT_APP_INTERVIEW_UI_URL;
    
    if (!interviewUiUrl) {
      setStatus(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Remove trailing slash if present, then append /api/health
      const baseUrl = interviewUiUrl.replace(/\/$/, '');
      const healthUrl = `${baseUrl}/api/health`;
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(healthUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setHealthData(data);
        setStatus(true);
      } else {
        setStatus(false);
        setHealthData(null);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Health check timeout');
      } else {
        console.error('Error checking Interview App health:', error);
      }
      setStatus(false);
      setHealthData(null);
    } finally {
      setLoading(false);
      setLastChecked(new Date());
    }
  };

  useEffect(() => {
    checkHealth();
  }, []);

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (e) {
      return timestamp;
    }
  };

  const selectedCompanyData = selectedCompany
    ? companies.find((c) => c.id === selectedCompany || c.id === Number(selectedCompany))
    : null;
  const verificationRequired = selectedCompanyData?.verification_required ?? false;
  const showingIdentity = selectedCompanyData?.showing_identity ?? false;

  const handleVerificationToggleClick = () => {
    if (!selectedCompany || !onVerificationRequiredChange) return;
    setPendingVerificationValue(!verificationRequired);
    setConfirmModalOpen(true);
  };

  const handleConfirmVerificationChange = async () => {
    if (!selectedCompany || !onVerificationRequiredChange || pendingVerificationValue === null) return;
    setConfirmModalOpen(false);
    setUpdatingVerification(true);
    try {
      await onVerificationRequiredChange(selectedCompany, pendingVerificationValue);
      setPendingVerificationValue(null);
    } finally {
      setUpdatingVerification(false);
    }
  };

  const handleCloseConfirmModal = () => {
    setConfirmModalOpen(false);
    setPendingVerificationValue(null);
  };

  const handleShowingIdentityToggleClick = () => {
    if (!selectedCompany || !onShowingIdentityChange || !verificationRequired) return;
    const newValue = !showingIdentity;
    (async () => {
      setUpdatingShowingIdentity(true);
      try {
        await onShowingIdentityChange(selectedCompany, newValue);
      } finally {
        setUpdatingShowingIdentity(false);
      }
    })();
  };

  return (
    <div className="space-y-6">
      {/* Verification Required section */}
      {onVerificationRequiredChange && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-amber-100 to-orange-100">
                <ShieldCheck className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('companyAdmins.verificationRequiredTitle')}</h3>
                <p className="text-sm text-gray-600 leading-relaxed max-w-2xl">{t('companyAdmins.verificationRequiredDescription')}</p>
              </div>
            </div>
          </div>
          {selectedCompany ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Column 1: Verification Required */}
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-gray-600">
                  {verificationRequired
                    ? t('companyAdmins.verificationRequiredOn')
                    : t('companyAdmins.verificationRequiredOff')}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={verificationRequired}
                  disabled={updatingVerification}
                  onClick={handleVerificationToggleClick}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    verificationRequired ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      verificationRequired ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {/* Column 2: Display Candidate Identity in Reports */}
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <span className="text-sm font-medium text-gray-700 block">
                    {t('companyAdmins.showingIdentityTitle')}
                  </span>
                  <span className="text-xs text-gray-500">
                    {verificationRequired
                      ? t('companyAdmins.showingIdentityDescription')
                      : t('companyAdmins.showingIdentityDisabledHint')}
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showingIdentity}
                  disabled={updatingShowingIdentity || !verificationRequired}
                  onClick={handleShowingIdentityToggleClick}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    !verificationRequired
                      ? 'bg-gray-200 cursor-not-allowed opacity-60'
                      : `cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${showingIdentity ? 'bg-blue-600' : 'bg-gray-200'}`
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      showingIdentity ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              {isSuperAdmin
                ? t('companyAdmins.selectScopeToEditVerification', 'Select a configuration scope above to view or change this setting.')
                : t('companyAdmins.selectCompanyToEditVerification')}
            </p>
          )}
        </div>
      )}

      <ConfirmationDialog
        open={confirmModalOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmVerificationChange}
        title={t('companyAdmins.verificationRequiredConfirmTitle')}
        message={
          selectedCompanyData?.name === 'Practice Company'
            ? t('companyAdmins.verificationRequiredConfirmMessageB2C', 'Do you want to change the verification required setting for Practice Interviews (B2C)?')
            : t('companyAdmins.verificationRequiredConfirmMessage')
        }
      />

      {/* Interview App health status */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100">
            <Activity className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{t('companyAdmins.interviewApp')}</h3>
            <p className="text-sm text-gray-600">{t('companyAdmins.serviceStatus')}</p>
          </div>
        </div>
        {loading ? (
          <RefreshCw className="h-5 w-5 text-gray-400 animate-spin" />
        ) : status ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : (
          <XCircle className="h-5 w-5 text-red-500" />
        )}
      </div>

      {loading && status === null ? (
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
        </div>
      ) : status ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm font-medium text-green-700">{t('companyAdmins.workingNormally')}</span>
          </div>
          {healthData && (
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
              {healthData.status && (
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{t('companyAdmins.status')}</span>
                  <span className="font-medium text-green-600 uppercase">{healthData.status}</span>
                </div>
              )}
              {healthData.uptime !== undefined && (
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{t('companyAdmins.uptime')}</span>
                  <span className="font-medium">{formatUptime(healthData.uptime)}</span>
                </div>
              )}
              {healthData.timestamp && (
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{t('companyAdmins.serverTime')}</span>
                  <span className="font-medium">{formatTimestamp(healthData.timestamp)}</span>
                </div>
              )}
            </div>
          )}
          {lastChecked && (
            <div className="text-xs text-gray-500 mt-2">
              {t('companyAdmins.lastChecked')} {lastChecked.toLocaleTimeString()}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-red-500" />
            <span className="text-sm font-medium text-red-700">{t('companyAdmins.notWorkingFine')}</span>
          </div>
          <button
            onClick={checkHealth}
            disabled={loading}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>{t('companyAdmins.checking')}</span>
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                <span>{t('companyAdmins.checkAgain')}</span>
              </>
            )}
          </button>
          {lastChecked && (
            <div className="text-xs text-gray-500 text-center">
              {t('companyAdmins.lastChecked')} {lastChecked.toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
    </div>
  );
};

export default AptiviewAppStatus;
